import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Minus } from "lucide-react";
import { api } from "@/lib/api";

/**
 * ChamberCompare — editorial side-by-side spec sheet for all three
 * TheraTec hyperbaric chambers. Pulls live product data from /api/products,
 * normalises spec labels (Max Pressure vs Peak Pressure, etc.), and renders
 * a sticky-header comparison table with per-column CTAs.
 */

const CHAMBER_SLUGS = [
  "hyperbaric-oxygen-chamber",
  "space-capsule-hyperbaric-chamber",
  "theratec-2-seater",
];

// Canonical rows — each row lists label aliases to look up in a product's specs.
// The first alias found wins. Fallback "—" if none match.
const ROWS = [
  { key: "capacity",     label: "Capacity",            aliases: ["Capacity"] },
  { key: "pressure",     label: "Peak Pressure",       aliases: ["Peak Pressure", "Max Pressure"] },
  { key: "session",      label: "Session Length",      aliases: ["Session Length", "Session"] },
  { key: "dimensions",   label: "Dimensions",          aliases: ["Dimensions"] },
  { key: "construction", label: "Shell Construction",  aliases: ["Shell Construction"] },
  { key: "redlight",     label: "NIR Red Light Panel", aliases: ["Red Light Panel"] },
  { key: "interior",     label: "Interior",            aliases: ["Interior"] },
  { key: "controls",     label: "Controls",            aliases: ["Controls", "Control"] },
  { key: "entertainment",label: "Entertainment",       aliases: ["Entertainment"] },
  { key: "viewing",      label: "Viewing",             aliases: ["Viewing", "Viewing Window"] },
  { key: "oxygen",       label: "Oxygen Delivery",     aliases: ["Oxygen"] },
  { key: "power",        label: "Power",               aliases: ["Power"] },
  { key: "colors",       label: "Finish Options",      aliases: ["Color Options", "Exterior Finishes", "Exterior Finish"] },
  { key: "delivery",     label: "Delivery",            aliases: ["Delivery"] },
  { key: "warranty",     label: "Warranty",            aliases: ["Warranty"] },
];

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const findSpec = (specs, aliases) => {
  if (!specs) return null;
  for (const a of aliases) {
    const hit = specs.find((s) => (s.label || "").toLowerCase() === a.toLowerCase());
    if (hit) return hit.value;
  }
  return null;
};

export default function ChamberCompare() {
  const [chambers, setChambers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/products");
        const products = data.products || [];
        const filtered = CHAMBER_SLUGS
          .map((slug) => products.find((p) => p.slug === slug))
          .filter(Boolean);
        if (!cancelled) setChambers(filtered);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Precompute row values per chamber
  const grid = useMemo(() => {
    return ROWS.map((row) => ({
      ...row,
      values: chambers.map((c) => findSpec(c.specs, row.aliases)),
    }));
  }, [chambers]);

  if (loading || chambers.length === 0) return null;

  return (
    <section
      id="compare"
      data-testid="chamber-compare"
      className="relative overflow-hidden bg-[#FAF9F6] py-24 lg:py-32 border-y border-[#E5E2DA]"
    >
      {/* soft halo */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60vw 40vw at 50% 0%, rgba(201,169,97,0.12), transparent 60%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="overline text-[#C9A961] inline-flex items-center gap-2">
            <span className="h-px w-6 bg-[#C9A961]" />
            Side-By-Side
            <span className="h-px w-6 bg-[#C9A961]" />
          </div>
          <h2 className="font-serif-editorial text-3xl md:text-4xl lg:text-5xl mt-6 leading-tight text-[#1C1C1C]">
            Three chambers.<br />
            <em className="italic tt-shimmer-gold">Pick without an email.</em>
          </h2>
          <p className="mt-6 text-sm md:text-base text-[#5C5C5C] leading-relaxed">
            Every spec that matters, side-by-side. Prices, pressure, footprint, finish options, warranty — laid out so your architect and CFO can decide in the same meeting.
          </p>
        </div>

        {/* Table container — horizontal scroll on mobile */}
        <div className="overflow-x-auto -mx-6 lg:mx-0 px-6 lg:px-0" data-testid="chamber-compare-scroll">
          <div className="min-w-[860px] lg:min-w-0">
            {/* Header row — hero images + prices */}
            <div className="grid grid-cols-[180px_1fr_1fr_1fr] gap-0 border border-[#E5E2DA] bg-white">
              <div className="p-6 flex items-end">
                <div className="overline text-[#5C5C5C]">Compare</div>
              </div>
              {chambers.map((c, i) => (
                <div
                  key={c.slug}
                  data-testid={`compare-col-${c.slug}`}
                  className={`p-6 flex flex-col ${i < chambers.length ? "border-l border-[#E5E2DA]" : ""}`}
                >
                  <div className="aspect-[4/3] w-full overflow-hidden bg-[#F5F3EE] mb-5">
                    <img
                      src={c.image || (c.images && c.images[0])}
                      alt={c.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="overline text-[#C9A961]">{c.category}</div>
                  <h3 className="font-serif-editorial text-xl lg:text-2xl mt-2 leading-tight text-[#1C1C1C]">
                    {c.name.replace("TheraTec ", "")}
                  </h3>
                  <div className="mt-4 pt-4 border-t border-[#E5E2DA] flex items-end justify-between">
                    <div>
                      <div className="overline text-[#5C5C5C]">From</div>
                      <div className="font-serif-editorial text-2xl mt-1 text-[#1C1C1C]">{fmt(c.price)}</div>
                      <div className="text-[11px] text-[#5C5C5C] mt-1 leading-tight">
                        <span className="tt-shimmer-gold font-medium">{fmt(c.finance_monthly || Math.round(c.price / 60))}</span>
                        <span className="text-[#A8A096]"> / mo · 60 mo</span>
                      </div>
                    </div>
                    <Link
                      to={`/product/${c.slug}`}
                      data-testid={`compare-explore-${c.slug}`}
                      className="group inline-flex items-center gap-2 overline text-[#C9A961] hover:gap-3 transition-all duration-300"
                    >
                      Explore
                      <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform duration-500" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Spec rows */}
            <div className="border-l border-r border-b border-[#E5E2DA]">
              {grid.map((row, rowIdx) => {
                // Detect if all three columns share the exact same value → mark as shared
                const nonNullVals = row.values.filter((v) => v);
                const allShared =
                  nonNullVals.length === row.values.length &&
                  nonNullVals.every((v) => v === nonNullVals[0]);

                return (
                  <div
                    key={row.key}
                    data-testid={`compare-row-${row.key}`}
                    className={`grid grid-cols-[180px_1fr_1fr_1fr] ${rowIdx % 2 === 0 ? "bg-[#FAF9F6]" : "bg-white"}`}
                  >
                    <div className="p-5 flex items-center gap-2 border-t border-[#E5E2DA]">
                      <span className="overline text-[#5C5C5C]">{row.label}</span>
                      {allShared && (
                        <span
                          title="Shared across all three"
                          className="inline-flex items-center justify-center h-4 w-4 rounded-full"
                          style={{ background: "rgba(201,169,97,0.15)", border: "1px solid rgba(201,169,97,0.5)" }}
                        >
                          <Check size={9} className="text-[#C9A961]" strokeWidth={2.5} />
                        </span>
                      )}
                    </div>
                    {row.values.map((val, ci) => (
                      <div
                        key={ci}
                        className="p-5 border-t border-l border-[#E5E2DA] text-sm text-[#1C1C1C] leading-relaxed"
                      >
                        {val ? (
                          <span>{val}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[#A8A096]">
                            <Minus size={12} />
                            <span className="text-xs">—</span>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Footer CTAs */}
            <div className="grid grid-cols-[180px_1fr_1fr_1fr] border-l border-r border-b border-[#E5E2DA] bg-white">
              <div className="p-5 overline text-[#5C5C5C] flex items-center">Take next step</div>
              {chambers.map((c) => (
                <div key={c.slug} className="p-5 border-l border-[#E5E2DA]">
                  <Link
                    to={`/product/${c.slug}`}
                    data-testid={`compare-cta-${c.slug}`}
                    className="block w-full text-center bg-[#1C1C1C] text-[#FAF9F6] py-3 text-sm tracking-wide hover:bg-[#C9A961] hover:text-[#1C1C1C] transition-colors duration-300"
                  >
                    Configure {c.name.replace("TheraTec ", "").split(" ")[0]}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Note */}
        <p className="mt-8 text-center text-xs text-[#A8A096] max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-1 mr-2">
            <span className="inline-flex items-center justify-center h-4 w-4 rounded-full" style={{ background: "rgba(201,169,97,0.15)", border: "1px solid rgba(201,169,97,0.5)" }}>
              <Check size={9} className="text-[#C9A961]" strokeWidth={2.5} />
            </span>
            marks specs shared across all three chambers.
          </span>
          Every unit ships with concierge crating, on-site installation, staff certification, and a launch-week protocol library.
        </p>
      </div>
    </section>
  );
}
